import React from 'react';
import { Link } from 'react-router-dom';
import { ScrollReveal } from '../components/ScrollReveal';
import { Monitor, Wrench, Leaf, Users, Brain, Heart, Hand, Activity } from 'lucide-react';

// Image URLs
const IMAGES = {
  reading: "https://customer-assets.emergentagent.com/job_roots-32-learners/artifacts/u572a801_IMG_0347.PNG",
  tablet: "https://customer-assets.emergentagent.com/job_roots-32-learners/artifacts/xcc7dquc_IMG_0364.JPG",
  robot: "https://customer-assets.emergentagent.com/job_roots-32-learners/artifacts/2jyoq9a0_IMG_0361.jpg",
  magnifying: "https://customer-assets.emergentagent.com/job_roots-32-learners/artifacts/gu9itucn_IMG_0348.JPG",
  gardening: "https://customer-assets.emergentagent.com/job_roots-32-learners/artifacts/jnksp1ed_IMG_0354.JPG",
  watering: "https://customer-assets.emergentagent.com/job_roots-32-learners/artifacts/8gu56dmd_IMG_0353.JPG",
  chickens: "https://customer-assets.emergentagent.com/job_roots-32-learners/artifacts/hwyxoe1c_IMG_0366.JPG",
  financial: "https://customer-assets.emergentagent.com/job_roots-32-learners/artifacts/fulclewv_IMG_0351.WEBP",
  classroom: "https://customer-assets.emergentagent.com/job_roots-32-learners/artifacts/rqxgj890_IMG_0358.JPG",
  greenhouse: "https://customer-assets.emergentagent.com/job_roots-32-learners/artifacts/0miovp04_IMG_0176%203.WEBP",
};

const fourPillars = [
  {
    name: 'HEAD',
    subtitle: 'Critical Thinking & Knowledge',
    icon: Brain,
    items: [
      'Philosophy for children (P4C) discussions',
      'Financial literacy: budgeting, saving, entrepreneurship',
      'Digital literacy: coding, robotics, AI ethics',
      'Research projects on local and global issues',
    ],
  },
  {
    name: 'HEART',
    subtitle: 'Emotional Intelligence & Community',
    icon: Heart,
    items: [
      'Morning circles and check-ins',
      'Conflict resolution and restorative practices',
      'Arts: music, drama, visual arts, storytelling',
      'Community service and nature connection',
    ],
  },
  {
    name: 'HANDS',
    subtitle: 'Trade, Craft & Practical Skills',
    icon: Hand,
    items: [
      'Agriculture: farm-to-table gardening, animal care',
      'Trade skills: carpentry, masonry, textiles',
      'Domestic arts: cooking, nutrition, meal planning',
      'Creative craft: pottery, weaving, traditional African crafts',
    ],
  },
  {
    name: 'HEALTH',
    subtitle: 'Physical Wellbeing & Movement',
    icon: Activity,
    items: [
      'Daily outdoor play and exploration',
      'Structured physical activities',
      'Body awareness and safety education',
      'Nutrition education tied to garden produce',
    ],
  },
];

const weeklyRhythm = [
  { day: 'Monday', morning: 'AI-powered literacy & numeracy', afternoon: 'HEAD: Entrepreneurship + Financial literacy' },
  { day: 'Tuesday', morning: 'AI-powered literacy & numeracy', afternoon: 'HANDS: Agriculture + Farm-to-table cooking' },
  { day: 'Wednesday', morning: 'AI-powered literacy & numeracy', afternoon: 'HEART: Arts + Community circle + Nature walk' },
  { day: 'Thursday', morning: 'AI-powered literacy & numeracy', afternoon: 'HANDS: Trade skills (carpentry/textiles)' },
  { day: 'Friday', morning: 'AI-powered literacy & numeracy', afternoon: 'HEAD + HEALTH: Project presentations + Sports' },
];

const comparison = [
  { traditional: '6-8 hours sitting at desks', novaRoots: '2 hours focused academics, 4 hours active doing' },
  { traditional: 'One-size-fits-all curriculum', novaRoots: 'Personalised AI paths + child-led inquiry' },
  { traditional: 'Homework after school', novaRoots: 'No traditional homework; daily reading encouraged' },
  { traditional: 'Teachers delivering content', novaRoots: 'Walezi facilitating discovery' },
  { traditional: 'Test scores measure success', novaRoots: 'Mastery demonstrated through application' },
  { traditional: 'Theoretical learning', novaRoots: 'Theory applied immediately in practice' },
];

export default function TheModel() {
  return (
    <div data-testid="model-page" className="page-transition pt-24">
      {/* Hero Section */}
      <section className="py-28 md:py-36 bg-[#F9F8F4]">
        <div className="container-nova">
          <div className="max-w-3xl">
            <ScrollReveal>
              <p className="text-sm font-medium uppercase tracking-wider text-[#C27856] mb-4">
                The Nova Roots Model
              </p>
            </ScrollReveal>
            
            <ScrollReveal delay={100}>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-[#1A3C34] leading-[1.1]">
                The 2+4 Model:<br />
                <span className="text-[#C27856]">Mastery Without Burnout</span>
              </h1>
            </ScrollReveal>
            
            <ScrollReveal delay={200}>
              <p className="mt-8 text-lg font-light leading-relaxed text-[#2D3A3A] max-w-2xl">
                Two hours of focused, AI-powered academic mastery. Four hours of hands-on, real-world learning. No traditional homework—just daily reading and termly projects. Deep, meaningful education that prepares children for life, not just exams.
              </p>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* The "2" Section */}
      <section data-testid="mastery-section" className="py-28 md:py-36 bg-[#F2F0E9]">
        <div className="container-nova">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-start">
            <div>
              <ScrollReveal>
                <div className="relative">
                  <span className="text-[8rem] md:text-[10rem] font-heading font-light text-[#1A3C34]/10 leading-none">2</span>
                  <div className="absolute bottom-4 left-0">
                    <p className="text-sm font-medium uppercase tracking-wider text-[#C27856]">
                      Morning Hours
                    </p>
                    <h2 className="font-serif text-3xl md:text-4xl font-normal text-[#1A3C34] mt-2">
                      Academic Mastery
                    </h2>
                  </div>
                </div>
              </ScrollReveal>
              
              <ScrollReveal delay={100}>
                <p className="mt-8 text-base font-light leading-relaxed text-[#2D3A3A]">
                  8:00 AM – 10:00 AM. Small pods of 6-8 children per Walezi. AI-assisted personalised learning paths.
                </p>
              </ScrollReveal>
              
              <ScrollReveal delay={150}>
                <div className="mt-8 space-y-4">
                  <div className="p-4 bg-white rounded-xl border border-[#EBE8E0]">
                    <p className="text-sm font-medium text-[#1A3C34]">Diagnostic Check-in (10 min)</p>
                    <p className="text-xs text-[#5C6B6B] mt-1">AI assesses yesterday's mastery and adjusts today's path</p>
                  </div>
                  <div className="p-4 bg-white rounded-xl border border-[#EBE8E0]">
                    <p className="text-sm font-medium text-[#1A3C34]">Literacy Block (50 min)</p>
                    <p className="text-xs text-[#5C6B6B] mt-1">Individualized reading, writing, comprehension at each child's level</p>
                  </div>
                  <div className="p-4 bg-white rounded-xl border border-[#EBE8E0]">
                    <p className="text-sm font-medium text-[#1A3C34]">Numeracy Block (50 min)</p>
                    <p className="text-xs text-[#5C6B6B] mt-1">Math concepts mastered through adaptive software + human guidance</p>
                  </div>
                  <div className="p-4 bg-white rounded-xl border border-[#EBE8E0]">
                    <p className="text-sm font-medium text-[#1A3C34]">Reflection & Goal-Setting (10 min)</p>
                    <p className="text-xs text-[#5C6B6B] mt-1">Child records progress, sets tomorrow's intention</p>
                  </div>
                </div>
              </ScrollReveal>
              
              <ScrollReveal delay={200}>
                <div className="mt-8 p-6 bg-[#1A3C34] rounded-xl">
                  <p className="text-sm font-light text-[#F9F8F4]">
                    <span className="text-[#C27856] font-medium">Mastery-based, not time-based:</span> Children advance when they demonstrate understanding, not when the calendar says so.
                  </p>
                </div>
              </ScrollReveal>
            </div>
            
            <ScrollReveal delay={150}>
              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-xl overflow-hidden">
                  <img 
                    src={IMAGES.reading}
                    alt="Child reading"
                    className="w-full h-64 md:h-72 object-cover"
                  />
                </div>
                <div className="rounded-xl overflow-hidden mt-12">
                  <img 
                    src={IMAGES.tablet}
                    alt="Child using tablet"
                    className="w-full h-64 md:h-72 object-cover"
                  />
                </div>
              </div>
              
              <div className="mt-8 p-6 bg-white rounded-xl border border-[#EBE8E0]">
                <p className="text-sm font-medium text-[#1A3C34] mb-3">What Parents Should Know</p>
                <p className="text-xs font-light text-[#5C6B6B] leading-relaxed">
                  "Your child will never be rushed ahead before they're ready, nor held back to wait for peers. The AI identifies exactly where they are and what they need next. But the Walezi ensures they feel seen, supported and challenged appropriately."
                </p>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* The "4" Section */}
      <section data-testid="domains-section" className="py-28 md:py-36 bg-[#F9F8F4]">
        <div className="container-nova">
          <ScrollReveal>
            <div className="relative mb-16">
              <span className="text-[8rem] md:text-[10rem] font-heading font-light text-[#1A3C34]/10 leading-none">4</span>
              <div className="absolute bottom-4 left-0">
                <p className="text-sm font-medium uppercase tracking-wider text-[#C27856]">
                  Afternoon Hours
                </p>
                <h2 className="font-serif text-3xl md:text-4xl font-normal text-[#1A3C34] mt-2">
                  Real-Life Learning
                </h2>
              </div>
            </div>
          </ScrollReveal>
          
          <ScrollReveal delay={100}>
            <p className="text-base font-light text-[#2D3A3A] max-w-2xl mb-12">
              10:30 AM – 3:30 PM (with breaks for meals, play and rest). Rotating through four pillars weekly. Project-based, inquiry-led, collaborative.
            </p>
          </ScrollReveal>

          <div className="grid md:grid-cols-2 gap-8">
            {fourPillars.map((pillar, index) => (
              <ScrollReveal key={pillar.name} delay={index * 100}>
                <div className="bg-white p-8 rounded-2xl border border-[#EBE8E0] h-full">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-12 h-12 rounded-full bg-[#1A3C34]/5 flex items-center justify-center">
                      <pillar.icon className="w-5 h-5 text-[#1A3C34]" />
                    </div>
                    <div>
                      <h3 className="font-heading text-xl font-medium text-[#1A3C34]">
                        {pillar.name}
                      </h3>
                      <p className="text-xs text-[#C27856]">{pillar.subtitle}</p>
                    </div>
                  </div>
                  
                  <ul className="space-y-2">
                    {pillar.items.map((item, itemIndex) => (
                      <li key={itemIndex} className="flex items-start gap-2 text-sm text-[#5C6B6B] font-light">
                        <span className="w-1 h-1 rounded-full bg-[#C27856] mt-2 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </ScrollReveal>
            ))}
          </div>
          
          {/* Agriculture Images */}
          <ScrollReveal delay={200}>
            <div className="mt-12 grid grid-cols-3 gap-4">
              <div className="rounded-xl overflow-hidden">
                <img src={IMAGES.gardening} alt="Gardening" className="w-full h-40 object-cover" />
              </div>
              <div className="rounded-xl overflow-hidden">
                <img src={IMAGES.watering} alt="Watering plants" className="w-full h-40 object-cover" />
              </div>
              <div className="rounded-xl overflow-hidden">
                <img src={IMAGES.chickens} alt="Animal care" className="w-full h-40 object-cover" />
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Weekly Rhythm */}
      <section className="py-28 md:py-36 bg-[#F2F0E9]">
        <div className="container-nova">
          <ScrollReveal>
            <h2 className="font-serif text-3xl md:text-4xl font-normal text-[#1A3C34] text-center mb-16">
              Weekly Rhythm
            </h2>
          </ScrollReveal>
          
          <ScrollReveal delay={100}>
            <div className="max-w-4xl mx-auto bg-white rounded-2xl border border-[#EBE8E0] overflow-hidden">
              <div className="grid grid-cols-3 bg-[#1A3C34] text-[#F9F8F4] text-sm font-medium">
                <div className="p-4">Day</div>
                <div className="p-4">Morning (2 hrs)</div>
                <div className="p-4">Afternoon (4 hrs)</div>
              </div>
              {weeklyRhythm.map((row, index) => (
                <div key={index} className="grid grid-cols-3 border-b border-[#EBE8E0] last:border-b-0">
                  <div className="p-4 text-sm font-medium text-[#1A3C34]">{row.day}</div>
                  <div className="p-4 text-xs text-[#5C6B6B]">{row.morning}</div>
                  <div className="p-4 text-xs text-[#5C6B6B]">{row.afternoon}</div>
                </div>
              ))}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* A Typical Day */}
      <section data-testid="typical-day-section" className="py-28 md:py-36 bg-[#F9F8F4]">
        <div className="container-nova">
          <div className="max-w-xl mx-auto">
            <ScrollReveal>
              <h2 className="font-serif text-3xl md:text-4xl font-normal text-[#1A3C34] text-center mb-16">
                A Typical Day
              </h2>
            </ScrollReveal>
            
            <ScrollReveal delay={100}>
              <div className="space-y-0">
                {[
                  { time: '8:00–10:00', activity: 'Mastery Block (Literacy & Numeracy)' },
                  { time: '10:00–10:30', activity: 'Break & Snack' },
                  { time: '10:30–12:00', activity: 'Applied Learning (HEAD/HANDS rotation)' },
                  { time: '12:00–1:00', activity: 'Lunch & Free Play' },
                  { time: '1:00–3:00', activity: 'Projects, HEART/HEALTH activities' },
                  { time: '3:00–3:30', activity: 'Reflection & Dismissal' },
                ].map((item, index) => (
                  <div 
                    key={index} 
                    className="flex items-baseline gap-10 py-5 border-b border-[#1A3C34]/10 last:border-b-0"
                  >
                    <span className="text-base font-medium text-[#C27856] tracking-wide w-32 flex-shrink-0">
                      {item.time}
                    </span>
                    <span className="text-[#1A3C34] font-light text-base">
                      {item.activity}
                    </span>
                  </div>
                ))}
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-28 md:py-36 bg-[#F2F0E9]">
        <div className="container-nova">
          <ScrollReveal>
            <h2 className="font-serif text-3xl md:text-4xl font-normal text-[#1A3C34] text-center mb-16">
              The 2+4 Difference
            </h2>
          </ScrollReveal>
          
          <ScrollReveal delay={100}>
            <div className="max-w-4xl mx-auto">
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="bg-[#5C6B6B] text-white p-4 rounded-t-xl text-center text-sm font-medium">
                  Traditional School
                </div>
                <div className="bg-[#1A3C34] text-white p-4 rounded-t-xl text-center text-sm font-medium">
                  Nova Roots 2+4
                </div>
              </div>
              {comparison.map((row, index) => (
                <div key={index} className="grid grid-cols-2 gap-4 mb-2">
                  <div className="bg-white p-4 rounded-lg text-sm text-[#5C6B6B] font-light">
                    {row.traditional}
                  </div>
                  <div className="bg-white p-4 rounded-lg text-sm text-[#1A3C34] font-light border-l-2 border-[#C27856]">
                    {row.novaRoots}
                  </div>
                </div>
              ))}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Walezi Section */}
      <section className="py-28 md:py-36 bg-[#F9F8F4]">
        <div className="container-nova">
          <div className="max-w-3xl mx-auto">
            <ScrollReveal>
              <h2 className="font-serif text-3xl md:text-4xl font-normal text-[#1A3C34] text-center">
                The Walezi
              </h2>
              <p className="text-center text-sm text-[#C27856] mt-2">Swahili: Caregivers, Guides, Mentors</p>
            </ScrollReveal>
            
            <ScrollReveal delay={100}>
              <p className="mt-8 text-base font-light text-[#2D3A3A] text-center">
                Our educators are not traditional teachers. They are facilitators of inquiry, mentors who know each child's strengths, struggles and dreams.
              </p>
            </ScrollReveal>
            
            <ScrollReveal delay={200}>
              <div className="mt-12 grid md:grid-cols-2 gap-6">
                <div className="bg-white p-6 rounded-xl border border-[#EBE8E0]">
                  <p className="text-sm font-medium text-[#1A3C34] mb-4">Every Walezi is trained in:</p>
                  <ul className="space-y-2">
                    {[
                      'AI-assisted personalised learning platforms',
                      'Socratic questioning and inquiry methods',
                      'Trauma-informed practices and emotional support',
                      'At least one trade skill (agriculture, craft, technical)',
                      'African educational philosophy'
                    ].map((item, index) => (
                      <li key={index} className="flex items-start gap-2 text-xs text-[#5C6B6B] font-light">
                        <span className="w-1 h-1 rounded-full bg-[#C27856] mt-1.5 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="bg-[#1A3C34] p-6 rounded-xl">
                  <p className="text-sm font-medium text-[#F9F8F4] mb-4">A Walezi is:</p>
                  <ul className="space-y-2">
                    {[
                      'A facilitator of inquiry, not deliverer of content',
                      'A mentor who knows each child deeply',
                      'A practitioner skilled in technology and trades',
                      'A guardian of each child\'s dignity and journey'
                    ].map((item, index) => (
                      <li key={index} className="flex items-start gap-2 text-xs text-[#F9F8F4]/80 font-light">
                        <span className="w-1 h-1 rounded-full bg-[#C27856] mt-1.5 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-[#1A3C34]">
        <div className="container-nova text-center">
          <ScrollReveal>
            <h2 className="font-heading text-3xl md:text-4xl font-light text-[#F9F8F4] max-w-xl mx-auto">
              See the Academics
            </h2>
          </ScrollReveal>
          
          <ScrollReveal delay={250}>
            <Link 
              to="/academics" 
              className="mt-10 inline-block bg-[#F9F8F4] text-[#1A3C34] hover:bg-white px-10 py-4 rounded-full text-sm font-medium tracking-wide transition-all duration-300"
              data-testid="academics-cta-btn"
            >
              View Academics
            </Link>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
}
